import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { map, Observable } from 'rxjs';
import { ProfileService } from 'src/app/services/profile/profile.service';
import {
	KpiItem,
	selectedValuesFilters,
	ChartTimelineResponse,
	DashboardInconsistenciesPerDay,
} from 'src/app/util/interfaces/myAssetInterface';
import { DateService } from 'src/app/services/date/date.service';
import { ApiResponse } from 'src/app/util/interfaces/ApiResponse';

@Injectable({
	providedIn: 'root',
})
export class MyAssetService {
	private getListKpiUrl = environment.baseApiUrl + 'myasset/dashboard/kpi';
	private getChartDataUrl = environment.baseApiUrl + 'myasset/flow/timeline';
	private getInconsistenciesTimelineUrl = environment.baseApiUrl + 'myasset/dashboard/inconsistencies/timeline';
	private getCiTimelineUrl = environment.baseApiUrl + 'myasset/dashboard/ci/timeline';
	private getInconsistencyPerDayUrl = environment.baseApiUrl + 'myasset/dashboard/inconsistencyPerDay';
	private getAvailableCiTypesUrl = environment.baseApiUrl + 'myasset/ciFilter';
	private getLastNotificationsUrl = environment.baseApiUrl + 'myasset/lastNotifications';
	private getAllLocalitysURL = environment.baseApiUrl + 'location/search';
	private getCmdbExportUrl = environment.baseApiUrl + 'cmdbhp/export?general=&sortBy=id&sortDown=false&showActiveOnlyCis=true';
	private activeStatusListUrl = environment.baseApiUrl + 'myasset/activeStatusList';
	private exportMovementsUrl = environment.baseApiUrl + 'myasset/flow/assets';

	headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService,
		private ProfileService: ProfileService,
		protected DateService: DateService
	) {}

	getListKpi(selectValues: selectedValuesFilters): Observable<KpiItem[]> {
		let getListKpiUrl = `${this.getListKpiUrl}?period_type=${selectValues.filterDate}`;

		if (selectValues.startDate && selectValues.endDate) {
			getListKpiUrl += `&startDate=${encodeURIComponent(selectValues.startDate)}`;
			getListKpiUrl += `&endDate=${encodeURIComponent(selectValues.endDate)}`;
		}
		if (selectValues.ciType) {
			getListKpiUrl += `&ci_type=${encodeURIComponent(selectValues.ciType)}`;
		}
		if (selectValues.subType) {
			getListKpiUrl += `&subType=${encodeURIComponent(selectValues.subType)}`;
		}
		if (selectValues.location) {
			getListKpiUrl += `&location=${encodeURIComponent(selectValues.location)}`;
		}

		getListKpiUrl += `&show_mine=${selectValues.show_mine}`;

		return this.http.get<any>(getListKpiUrl, { headers: this.headers });
	}

	// Chart de Movimentações
	getDataChart(selectValues: selectedValuesFilters): Observable<ChartTimelineResponse> {
		let getChartDataUrl = this.getChartDataUrl;
		getChartDataUrl = `${getChartDataUrl}?period_type=${selectValues.filterDate}`;

		if (selectValues.startDate && selectValues.endDate) {
			getChartDataUrl += `&startDate=${encodeURIComponent(selectValues.startDate)}`;
			getChartDataUrl += `&endDate=${encodeURIComponent(selectValues.endDate)}`;
		}
		if (selectValues.ciType) {
			getChartDataUrl += `&ci_type=${encodeURIComponent(selectValues.ciType)}`;
		}
		if (selectValues.subType) {
			getChartDataUrl += `&subType=${encodeURIComponent(selectValues.subType)}`;
		}
		if (selectValues.location) {
			getChartDataUrl += `&location=${encodeURIComponent(selectValues.location)}`;
		}

		getChartDataUrl += `&show_mine=${selectValues.show_mine}`;

		return this.http.get<ChartTimelineResponse>(getChartDataUrl, { headers: this.headers }).pipe(
			map((response) => {
				console.log('Chart de Movimentações - Response:', response);
				if (
					selectValues.filterDate === 'week' ||
					selectValues.filterDate === 'month' ||
					(selectValues.filterDate === 'custom' && selectValues.customDate)
				) {
					response.labels = response.labels.map((label) => {
						// use o this.DateService.formatDate para formatar a data como preferir, por exemplo:
						return this.DateService.formatDate(this.convertDDMMYYtoYYYYMMDD(label.toString()));
					});
				}
				return response;
			})
		);
	}

	// Chart de Inconsistências
	getInconsistenciesTimeline(filters: selectedValuesFilters, inconsistencyTypeId?: number): Observable<ChartTimelineResponse> {
		let url = `${this.getInconsistenciesTimelineUrl}?period_type=${filters.filterDate}`;

		if (filters.startDate && filters.endDate) {
			url += `&start_date=${encodeURIComponent(filters.startDate)}`;
			url += `&end_date=${encodeURIComponent(filters.endDate)}`;
		}

		if (inconsistencyTypeId !== undefined) {
			url += `&type_id=${inconsistencyTypeId}`;
		}

		return this.http.get<ChartTimelineResponse>(url, { headers: this.headers }).pipe(
			map((response) => {
				if (
					filters.filterDate === 'week' ||
					filters.filterDate === 'month' ||
					(filters.filterDate === 'custom' && filters.startDate && filters.endDate)
				) {
					response.labels = response.labels.map((label) => {
						// use o this.DateService.formatDate para formatar a data
						return this.DateService.formatDate(this.convertDDMMYYtoYYYYMMDD(label.toString()));
					});
				}
				return response;
			})
		);
	}

	// Chart de quantidade de CIs em aberto por dia
	getCiTimeline(selectValues: selectedValuesFilters): Observable<ChartTimelineResponse> {
		let url = `${this.getCiTimelineUrl}?period_type=${selectValues.filterDate}`;

		if (selectValues.startDate && selectValues.endDate) {
			url += `&startDate=${encodeURIComponent(selectValues.startDate)}`;
			url += `&endDate=${encodeURIComponent(selectValues.endDate)}`;
		}

		if (selectValues.ciType) {
			url += `&ci_type=${encodeURIComponent(selectValues.ciType)}`;
		}

		if (selectValues.location) {
			url += `&location=${encodeURIComponent(selectValues.location)}`;
		}

		url += `&show_mine=${selectValues.show_mine}`;

		return this.http.get<ChartTimelineResponse>(url, { headers: this.headers }).pipe(
			map((response) => {
				if (
					selectValues.filterDate === 'week' ||
					selectValues.filterDate === 'month' ||
					(selectValues.filterDate === 'custom' && selectValues.customDate)
				) {
					response.labels = response.labels.map((label) => {
						// use o this.DateService.formatDate
						return this.DateService.formatDate(this.convertDDMMYYtoYYYYMMDD(label.toString()));
					});
				}
				return response;
			})
		);
	}

	getInconsistencyPerDay(startDate: string, endDate: string): Observable<ApiResponse<DashboardInconsistenciesPerDay>> {
		const url = `${this.getInconsistencyPerDayUrl}`;
		const parameters = {
			startDate: startDate,
			endDate: endDate,
		};
		return this.http.get<ApiResponse<DashboardInconsistenciesPerDay>>(url, { headers: this.headers, params: parameters });
	}

	getAvailablesCiTypes(): Observable<any> {
		const getAvailableCiTypesUrl = this.getAvailableCiTypesUrl;
		return this.http.get<any>(getAvailableCiTypesUrl, { headers: this.headers });
	}

	getAvailablesLocations(): Observable<any[]> {
		const defaultCustomer = this.ProfileService.userData().idCustomer;
		const getAllLocalitysURL = `${this.getAllLocalitysURL}?customer=${defaultCustomer}`;

		return this.http.get<any[]>(getAllLocalitysURL, { headers: this.headers }).pipe(
			map((localityRes) => {
				const newLocalityArray = [];
				for (const resp of localityRes) {
					const newLocality = {
						id_location: resp.id,
						location: resp.location,
						id_site: resp.site,
						site: resp.name_site,
					};
					newLocalityArray.push(newLocality);
				}

				return newLocalityArray;
			})
		);
	}

	exportData(selectValues: selectedValuesFilters): Observable<any> {
		let getCmdbExportUrl = this.getCmdbExportUrl;
		if (selectValues.ciType) {
			getCmdbExportUrl += `&type=${encodeURIComponent(selectValues.ciType)}`;
		}

		if (selectValues.subType) {
			getCmdbExportUrl += `&sub_type=${encodeURIComponent(selectValues.subType)}`;
		}

		if (selectValues.location) {
			getCmdbExportUrl += `&location=${encodeURIComponent(selectValues.location)}`;
		}

		getCmdbExportUrl += `&status=${encodeURIComponent(selectValues.status)}`;

		getCmdbExportUrl += `&active=1`;

		return this.http.get<any>(getCmdbExportUrl, { headers: this.headers });
	}

	exportMovements(selectValues: selectedValuesFilters): Observable<any> {
		let exportMovementsUrl = `${this.exportMovementsUrl}?period_type=${selectValues.filterDate}`;

		if (selectValues.filterDate === 'custom' && selectValues.customDate) {
			const [startDate, endDate] = selectValues.customDate.split(' - ');
			exportMovementsUrl += `&startDate=${encodeURIComponent(startDate)}`;
			exportMovementsUrl += `&endDate=${encodeURIComponent(endDate)}`;
		}
		if (selectValues.ciType) {
			exportMovementsUrl += `&ci_type=${encodeURIComponent(selectValues.ciType)}`;
		}
		if (selectValues.subType) {
			exportMovementsUrl += `&subType=${encodeURIComponent(selectValues.subType)}`;
		}
		if (selectValues.location) {
			exportMovementsUrl += `&location=${encodeURIComponent(selectValues.location)}`;
		}

		exportMovementsUrl += `&show_mine=${selectValues.show_mine}`;

		return this.http.get<any>(exportMovementsUrl, { headers: this.headers });
	}

	getActiveStatusList(): Observable<any> {
		const activeStatusListUrl = this.activeStatusListUrl;
		return this.http.get<any>(activeStatusListUrl, { headers: this.headers });
	}

	getLastNotifications(): Observable<any[]> {
		return this.http.get<any[]>(this.getLastNotificationsUrl, { headers: this.headers });
	}

	private convertDDMMYYtoYYYYMMDD(dateString: string): string {
		const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
		const match = dateString.match(datePattern);

		if (match) {
			const day = match[1].padStart(2, '0');
			const month = match[2].padStart(2, '0');
			let year = match[3];

			if (year.length === 2) {
				const currentYear = new Date().getFullYear();
				const century = Math.floor(currentYear / 100) * 100;
				year = String(century + Number.parseInt(year));
			}

			return `${year}-${month}-${day}`;
		}

		return dateString;
	}
}
